import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useAuth0 } from '@auth0/auth0-react';

import useMounted from 'src/hooks/useMounted';
import useApi from './useApi';
import { webApiConfig } from 'src/config';
import { history } from 'src/store';

const useFetchData = ({
  api,
  path,
  headers,
  auth,
  immediate = true,
  initialData = []
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { getAccessTokenSilently } = useAuth0();
  let accessToken = undefined;
  const isMounted = useMounted();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiPath, setPath] = useState(path);
  const apiInstance = api ? api : useApi({ baseUri: webApiConfig.baseUri.bff });

  const [result, setResult] = useState({
    data: initialData,
    header: {}
  });

  const noticeError = (err) => {
    /**
     * BFFから取得したエラーの場合のみエラースナックバーを表示する
     * 非ログイン状態でAuth0のgetAccessTokenSilentlyに失敗した場合などは
     * ログイン画面に遷移する前にエラースナックバーが表示されてしまい、ユーザーに
     * 意図せぬ不安を与えてしまう可能性があるためこの方法を採用している。
     * 将来的にはログインの仕組み自体を見直しこの分岐をやめたい
     */
    const errorCode = err.error?.code;
    if (errorCode) {
      let errorMessage;

      switch (errorCode) {
        case 40110:
          errorMessage = 'データを取得するための権限がありません。';
          history.push('/error/401');
          break;
        case 40400:
          errorMessage = 'データが見つかりませんでした。';
          history.push('/error/404');
          break;
        default:
          errorMessage = 'データを取得時にエラーが発生しました。';
          history.push('/error/500');
          break;
      }
      enqueueSnackbar(errorMessage, {
        variant: 'error'
      });
    }
  };

  const handleGetData = useCallback(
    async (path) => {
      setLoading(true);
      setError(null);

      try {
        if (auth) {
          accessToken = await getAccessTokenSilently();
        }

        const response = await apiInstance.get(path ? path : apiPath, {
          headers: auth
            ? { ...headers, Authorization: `Bearer ${accessToken}` }
            : headers
        });

        if (isMounted()) {
          setResult({
            data: response.data,
            header: response.headers
          });
        }
      } catch (err) {
        setError(err);
        noticeError(err);
      }

      setLoading(false);
    },
    [isMounted, path]
  );

  useEffect(() => {
    if (immediate && apiPath) {
      handleGetData();
    }
  }, [apiPath]);

  return { result, loading, error, setPath, handleGetData };
};

export default useFetchData;
